import { useContext, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { AppContext } from "../../../pages/root/Root";
import Grid from "@mui/material/Unstable_Grid2";
import {
  Paper,
  Typography,
  Backdrop,
  IconButton,
  CircularProgress,
  Button,
  Box,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
} from "@mui/material";
import Modal from "@mui/material/Modal";
import {
  Close as CloseIcon,
  Refresh,
  Download,
  ViewCarouselOutlined,
  GridViewOutlined,
  ViewWeek,
} from "@mui/icons-material";

import { AppAlert } from "../../common/appAlert/AppAlert";
import { DataTable } from "../../common/dataTable/DataTable";
import { CreativeAnalysisFilters } from "../creativeAnalysisFilters/CreativeAnalysisFilters";
import { UpdateCreative } from "../../common/updateCreative/UpdateCreative";
import { UpdateCreativeList } from "../../common/updateCreativeList/UpdateCreativeList";

import useCreativeAnalysis from "../../../hooks/eRetail/useCreativeAnalysis";

import { initLocalization, downloadFile } from "../../../utils/helpers";
import { creativeAnalysisStrings } from "./locale";
import {
  DEFAULT_TABLE_PAGINATION,
  DEFAULT_E_RETAIL_CREATIVE_ANALYSIS_FILTERS,
  DEFAULT_CREATIVE_ANALYSIS_SORTING,
  DEFAULT_CREATIVE_ANALYSIS_COLUMNS,
} from "../../../constants";

export const CreativeAnalysis = ({ dataSource }) => {
  initLocalization(creativeAnalysisStrings);

  const { language } = useContext(AppContext);

  const [error, setError] = useState("");
  const [filtersVersion, setFiltersVersion] = useState(1);
  const [filters, setFilters] = useState(
    DEFAULT_E_RETAIL_CREATIVE_ANALYSIS_FILTERS,
  );
  const [pagination, setPagination] = useState(DEFAULT_TABLE_PAGINATION);
  const [columns, setColumns] = useState(DEFAULT_CREATIVE_ANALYSIS_COLUMNS);
  const [filteredColumns, setFilteredColumns] = useState(
    DEFAULT_CREATIVE_ANALYSIS_COLUMNS,
  );
  const [sorting, setSorting] = useState(DEFAULT_CREATIVE_ANALYSIS_SORTING);
  const [openModal, setOpenModal] = useState(false);
  const [listView, setListView] = useState(false);

  const {
    isLoading,
    isExporting,
    creatives,
    getCreatives,
    setCreatives,
    exportCreatives,
    creativeDetails,
    getCreativeDetails,
  } = useCreativeAnalysis();

  const creativesData = useMemo(() => {
    return creatives?.data || [];
  }, [creatives]);

  const tableColumns = useMemo(() => {
    const filtered = DEFAULT_CREATIVE_ANALYSIS_COLUMNS.filter((col) =>
      columns.includes(col),
    );
    const cols = filtered.map((col) => ({
      field: col,
      headerName: creativeAnalysisStrings[col],
    }));

    const creativesCount = {
      field: "creativesCount",
      headerName: creativeAnalysisStrings.creativesCount,
    };

    if (columns.includes("status")) {
      cols.splice(cols.length - 1, 0, creativesCount);
    } else {
      cols.push(creativesCount);
    }

    return cols;
  }, [columns, language]);

  const getColumnsGroup = () => columns.map((col) => col.toLowerCase());

  const handleCloseError = () => setError(false);

  const handleResetFilters = () => {
    setFilters(DEFAULT_E_RETAIL_CREATIVE_ANALYSIS_FILTERS);
    setFiltersVersion((prev) => prev + 1);
    setPagination({ ...pagination, pageNumber: 0 });
    setCreatives([]);
  };

  const handleSearch = async () => {
    setCreatives([]);
    await getCreatives({
      ...pagination,
      pageNumber: 1,
      ...filters,
      ...sorting,
      groupByColumns: getColumnsGroup(),
    });
    setPagination({ ...pagination, pageNumber: 0 });
  };

  const handleExport = () => {
    exportCreatives({
      ...filters,
      ...sorting,
      groupByColumns: getColumnsGroup(),
      userLanguage: language,
    })
      .then((data) => {
        downloadFile(
          `eretail-${creativeAnalysisStrings.title
            .replaceAll(" ", "-")
            .toLowerCase()}`,
          "xlsx",
          data,
        );
      })
      .catch((error) => {
        setError(error);
      });
  };

  const handleSelectRow = (e, index) => {
    const { creativesCount, ...rowColumns } = creativesData[index];
    const selectedRowColumns = Object.keys(rowColumns).map((key) => ({
      columnName: key,
      columnValue: creativesData[index][key],
    }));

    getCreativeDetails({
      ...filters,
      selectedRowColumns,
    })
      .then(() => {
        setOpenModal(true);
      })
      .catch((error) => {
        setError(error);
      });
  };

  const handleSort = async (columnId, direction) => {
    if (!creativesData.length) {
      return;
    }

    const sortColumn = columnId.charAt(0).toUpperCase() + columnId.slice(1);

    await getCreatives({
      ...pagination,
      pageNumber: 1,
      ...filters,
      groupByColumns: getColumnsGroup(),
      sortOption: { sortColumn, direction },
    });
    setPagination({ ...pagination, pageNumber: 0 });
    setSorting({ ...sorting, sortOption: { sortColumn, direction } });
  };

  const handleChangePage = async (event, newPage) => {
    await getCreatives({
      ...pagination,
      pageNumber: newPage + 1,
      ...filters,
      ...sorting,
      groupByColumns: getColumnsGroup(),
    });
    setPagination({ ...pagination, pageNumber: newPage });
  };

  const handleChangeRowsPerPage = async (event) => {
    const value = parseInt(event.target.value, 10);
    await getCreatives({
      ...pagination,
      pageNumber: 1,
      pageSize: value,
      ...filters,
      ...sorting,
      groupByColumns: getColumnsGroup(),
    });
    setPagination({ ...pagination, pageNumber: 0, pageSize: value });
  };

  const handleSelectColumns = (event) => {
    setFilteredColumns(event.target.value);
  };

  const handleUpdateColumns = async () => {
    await getCreatives({
      ...pagination,
      pageNumber: 1,
      ...filters,
      ...sorting,
      groupByColumns: filteredColumns.map((col) => col.toLowerCase()),
    });
    setPagination({ ...pagination, pageNumber: 0 });
    setColumns(filteredColumns);
  };

  const handleResetColumns = async () => {
    await getCreatives({
      ...pagination,
      pageNumber: 1,
      ...filters,
      ...sorting,
      groupByColumns: DEFAULT_CREATIVE_ANALYSIS_COLUMNS.map((col) =>
        col.toLowerCase(),
      ),
    });
    setPagination({ ...pagination, pageNumber: 0 });
    setFilteredColumns(DEFAULT_CREATIVE_ANALYSIS_COLUMNS);
    setColumns(DEFAULT_CREATIVE_ANALYSIS_COLUMNS);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  return (
    <Paper sx={{ padding: "0.5rem", height: "100%", position: "relative" }}>
      <AppAlert
        key={error}
        open={Boolean(error)}
        type="error"
        onClose={handleCloseError}
      >
        {creativeAnalysisStrings.errors[error]}
      </AppAlert>

      <Grid container direction="column" height="100%">
        <Grid xs={12} container justifyContent="space-between" minHeight="32px">
          <Typography>{creativeAnalysisStrings.title}</Typography>
          <Grid container>
            <Button
              startIcon={
                isExporting ? (
                  <CircularProgress size={18} sx={{ opacity: 0.5 }} />
                ) : (
                  <Download />
                )
              }
              variant="outlined"
              disabled={!creativesData?.length || isExporting}
              onClick={handleExport}
              sx={{ ml: 2 }}
            >
              {creativeAnalysisStrings.exportButton}
            </Button>
            <Button
              sx={{ ml: 2 }}
              onClick={handleResetFilters}
              startIcon={<Refresh sx={{ transform: "scaleX(-1)" }} />}
              variant="text"
            >
              {creativeAnalysisStrings.resetFiltersButton}
            </Button>
          </Grid>
        </Grid>

        <Grid xs={12} paddingY={3}>
          <CreativeAnalysisFilters
            key={filtersVersion}
            filters={filters}
            setFilters={setFilters}
            onSearch={handleSearch}
          />
        </Grid>

        <Grid xs={12} display="flex" paddingY={0.5}>
          <Box>
            <FormControl size="small">
              <Select
                labelId="cols-label"
                id="cols-select"
                displayEmpty
                multiple
                value={filteredColumns}
                onChange={handleSelectColumns}
                renderValue={() => creativeAnalysisStrings.filterColumns}
              >
                {DEFAULT_CREATIVE_ANALYSIS_COLUMNS.map((col) => (
                  <MenuItem key={col} value={col}>
                    <Checkbox checked={filteredColumns.includes(col)} />
                    <ListItemText primary={creativeAnalysisStrings[col]} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Button
            sx={{ ml: 2 }}
            variant="contained"
            color="secondary"
            onClick={handleUpdateColumns}
            startIcon={<ViewWeek />}
            disabled={!filteredColumns.length}
          >
            {creativeAnalysisStrings.update}
          </Button>

          <Box display="inline-flex" justifyContent="flex-end" flexGrow="1">
            <Button
              sx={{ ml: 2 }}
              onClick={handleResetColumns}
              startIcon={<Refresh sx={{ transform: "scaleX(-1)" }} />}
              variant="text"
              disabled={
                columns.length === DEFAULT_CREATIVE_ANALYSIS_COLUMNS.length
              }
            >
              {creativeAnalysisStrings.resetColumns}
            </Button>
          </Box>
        </Grid>

        <Grid xs={12} display="flex" flexDirection="column" flexGrow="1">
          <DataTable
            columns={tableColumns}
            rows={creativesData}
            page={pagination.pageNumber}
            rowsPerPage={pagination.pageSize}
            totalRecords={creatives.totalRecords}
            allowSelection={true}
            showNoDataMessage={false}
            onSelectRow={handleSelectRow}
            sortBy={
              sorting.sortOption.sortColumn.charAt(0).toLowerCase() +
              sorting.sortOption.sortColumn.slice(1)
            }
            sortDirection={sorting?.sortOption?.direction}
            sortableColumns={tableColumns.map((item) => item.field)}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            onSort={handleSort}
          />
        </Grid>
      </Grid>

      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-describedby="view-creatives-modal"
        sx={{
          "&": { zIndex: (theme) => theme.zIndex.drawer - 1, top: 50 },
          "& .MuiModal-backdrop": { top: 50 },
        }}
      >
        <Grid
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100vw",
            height: "calc(100vh - 50px)",
            bgcolor: "#fff",
          }}
          className="view-creatives-modal"
          id="view-creatives-modal"
        >
          <IconButton
            onClick={handleCloseModal}
            sx={{ position: "absolute", top: 10, right: 10 }}
          >
            <CloseIcon color="primary" sx={{ width: 20, height: 20 }} />
          </IconButton>
          <Button
            startIcon={
              listView ? <ViewCarouselOutlined /> : <GridViewOutlined />
            }
            size="small"
            variant="outlined"
            onClick={() => setListView(!listView)}
            sx={{ position: "absolute", top: 10, left: 50 }}
          >
            {listView
              ? creativeAnalysisStrings.carouselView
              : creativeAnalysisStrings.listView}
          </Button>
          {listView ? (
            <UpdateCreativeList
              dataSource={dataSource}
              creativeDetailsData={creativeDetails}
              showProductListing={false}
              onClose={handleCloseModal}
            />
          ) : (
            <UpdateCreative
              dataSource={dataSource}
              creativeDetailsData={creativeDetails}
              showProductListing={false}
              onClose={handleCloseModal}
            />
          )}
        </Grid>
      </Modal>

      <Backdrop
        open={isLoading}
        sx={{
          position: "absolute",
          color: "#fff",
          borderRadius: 1,
          zIndex: (theme) => theme.zIndex.modal + 1,
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Paper>
  );
};

CreativeAnalysis.propTypes = {
  dataSource: PropTypes.string.isRequired,
};
